// hooks/useFindList.ts
import { useQuery, UseQueryOptions, QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { httpRequest } from "../helpers/api";
import { BaseResponseProps } from "../types/config.type";

type UseFetchProps<TData> = {
	endpoint: string;
	params?: any;
	options?: Omit<UseQueryOptions<TData, Error, TData>, "queryKey" | "queryFn">;
	isEnabled?: boolean;
};

type UseFetchResponse<TData> = {
	data: TData | undefined;
	isLoading: boolean;
	fetchList: (options?: RefetchOptions) => Promise<QueryObserverResult<TData, Error>>;
};

export function useFetchQuery<TData>({
	endpoint,
	params,
	options,
	isEnabled = true,
}: UseFetchProps<TData>): UseFetchResponse<TData> {
	async function fetchFn() {
		const res = await httpRequest.get<BaseResponseProps<TData>>(endpoint, {
			params,
		});
		return res.data.payload;
	}

	const { data, refetch, isFetching } = useQuery<TData, Error, TData>({
		queryKey: [endpoint, params],
		queryFn: fetchFn,
		enabled: isEnabled,
		staleTime: 0,
		cacheTime: 0, // v4 pakai cacheTime, bukan gcTime
		refetchOnMount: "always",
		refetchOnWindowFocus: false,
		...options,
	});

	return {
		data: data as TData,
		isLoading: isFetching,
		fetchList: refetch,
	};
}

export default useFetchQuery;
